// src/components/WithdrawPlanyCoins.jsx

import React, { useContext, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../auth/auth-context';
import { GET_WALLET_SUMMARY } from '../../graphql/queries/tip';
import { rootBkndPath } from '../../util/link';
import PaymentSettings from '../../pages/customer/booking/paymentSettings';
import { GET_PAYMENTS } from '../../graphql/queries';
import { fetchFeeConfig } from '../../api/fees';
import { useCoinConfig } from './tipProvider';
import { SEND_EMAIL_MUTATION } from '../../graphql/mutations';

function WithdrawButton({ amount, onSuccess, onError, disabled }) {
  const [loading, setLoading] = useState(false);


  const handleClick = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        onError('Authentication required. Please log in.');
        return;
      }
      const res = await fetch(`${rootBkndPath}/api/payout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ amount }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Payout failed');
      onSuccess(json);
    } catch (err) {
      onError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      onClick={handleClick}
      disabled={disabled || loading}
    >
      {loading ? <CircularProgress size={20} /> : 'Withdraw'}
    </Button>
  );
}

export default function WithdrawPlanyCoins() {
  const { businessUrl, staffUrl } = useParams();
  const { providerUser } = useContext(UserContext);
  const navigate = useNavigate();
  // Local UI state
  const { minWithdrawal } = useCoinConfig();

  const [amount, setAmount] = useState(minWithdrawal);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Fee config state
  const [feeConfig, setFeeConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState('');

  // Fetch dynamic fee constants
  useEffect(() => {
    fetchFeeConfig()
      .then(cfg => {
        setFeeConfig(cfg);
        setConfigLoading(false);
      })
      .catch(err => {
        setConfigError('Failed to load fee settings.');
        setConfigLoading(false);
      });
  }, []);

  // Fetch wallet & payments
  const { data, loading, error } = useQuery(GET_WALLET_SUMMARY, {
    variables: { staffId: providerUser?.staffId },
    skip: !providerUser?.staffId,
  });
  const {
    data: paymentsData,
    loading: paymentsLoading,
    error: paymentsError
  } = useQuery(GET_PAYMENTS, {
    variables: { businessUrl },
    skip: !businessUrl,
  });

  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);

  // Show spinner if anything is loading
  if (loading || paymentsLoading || configLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }
  // Handle errors
  if (error) return <Alert severity="error">Error loading wallet.</Alert>;
  if (paymentsError) return <Alert severity="error">Error loading payment methods.</Alert>;
  if (configError) return <Alert severity="error">{configError}</Alert>;

  const balance = data.getWalletSummary.balance;
  const {
    redemptionRateUsd,
    platformFeeRate,
    paypalFeeRate,
    paypalFlatFeeUsd
  } = feeConfig;

  // 1️⃣ Gross USD based on the redemption rate:
  const grossUsd = amount * redemptionRateUsd;

  // 2️⃣ Calculate each fee:
  const platformFee = grossUsd * platformFeeRate;
  const paypalPctFee = grossUsd * paypalFeeRate;
  const totalFees = platformFee + paypalPctFee + paypalFlatFeeUsd;

  // 3️⃣ Final net USD:
  const netUsd = grossUsd - totalFees;

  // Check for PayPal method
  const hasPayPal = paymentsData.payments.some(
    p => p.paymentMethod.type.toLowerCase() === 'paypal'
  );

  const handleSuccess = resp => {
    setSuccessMsg(resp.message);
    setSuccessOpen(true);

    const coinAmt = amount;
    const usdNet = netUsd.toFixed(2);
    const userEmail = providerUser.email;

    sendEmail({
      variables: {
        from_email: 'no-reply@plany365.com',
        to: userEmail,
        subject: `PlanyCoin payout – ${coinAmt} coins sent to your PayPal`,
        text: `You transferred ${coinAmt} PlanyCoins from your Plany365 wallet to your PayPal account.\nAmount received: $${usdNet}.`,
        html: `<p>You transferred <strong>${coinAmt} PlanyCoins</strong> from your Plany365 wallet to your PayPal account.</p><p>Amount received: <strong>$${usdNet}</strong>.</p>`,
      },
    }).catch(console.error);

    sendEmail({
      variables: {
        from_email: 'no-reply@plany365.com',
        to: 'billing@plany365.com',
        subject: `Wallet → PayPal payout – ${coinAmt} coins by ${userEmail}`,
        text: `${userEmail} transferred ${coinAmt} PlanyCoins to PayPal (net $${usdNet}).`,
        html: `${userEmail} transferred <strong>${coinAmt}</strong> PlanyCoins to PayPal (net $${usdNet}).`,
      },
    }).catch(console.error);
  };
  const handleError = msg => setErrorMsg(msg);
  const handleSuccessOk = () => {
    setSuccessOpen(false);
    navigate(`/${businessUrl}/${staffUrl}/wallet`, { state: { didWithdraw: true } });
  };
  const handleSettingsOpen = () => setSettingsOpen(true);
  const handleSettingsClose = () => setSettingsOpen(false);

  // Disable withdraw button if invalid
  const disableWithdraw =
    amount < minWithdrawal ||
    amount > balance ||
    netUsd <= 0;

  return (
    <>
      <Dialog open fullWidth maxWidth="sm">
        <DialogTitle>Withdraw PlanyCoins</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography>Balance: {balance} PlanyCoins</Typography>
            <TextField
              fullWidth
              type="number"
              label="Coins to Withdraw"
              InputProps={{ inputProps: { min: minWithdrawal, max: balance } }}
              value={amount}
              onChange={e => setAmount(Number(e.target.value))}
              helperText={`Minimum ${minWithdrawal} coins`}
              sx={{ mt: 1 }}
            />
            <Typography sx={{ mt: 2 }}>
              Net USD after fees: ${netUsd.toFixed(2)}
            </Typography>
          </Box>

          {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

          {!hasPayPal ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No PayPal payment method set.
              <Button variant="text" onClick={handleSettingsOpen} sx={{ ml: 1 }}>
                Configure now
              </Button>
            </Alert>
          ) : (
            <Box display="flex" justifyContent="flex-end">
              <WithdrawButton
                amount={amount}
                onSuccess={handleSuccess}
                onError={handleError}
                disabled={disableWithdraw}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate(`/${businessUrl}/${staffUrl}/wallet`)}>
            Back to Wallet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={handleSettingsClose} fullWidth maxWidth="md">
        <DialogTitle>Business Integration Settings</DialogTitle>
        <DialogContent dividers>
          <PaymentSettings isDisabled={false} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSettingsClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success Confirmation */}
      <Dialog open={successOpen} onClose={() => { }} maxWidth="xs" fullWidth>
        <DialogTitle>Withdrawal Successful</DialogTitle>
        <DialogContent>
          <Typography>{successMsg}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleSuccessOk}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
